import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { Box, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { MetaChip } from "../../../components/UIToolkit/Badges/MetaChip";
import { InlineHint } from "../../../components/UIToolkit/molecules/InlineHint";
import { FONT_SIZE, LETTER_SPACING } from "../../../theme/tokens";
import type { PbjBand, PbjCategoryScore } from "../../../utils/models";
import { bufferedCutoffFor, bufferPointsCost, cmsBandFor } from "./bandBuffer.utils";
import { brandTint, TABLE_HEAD_BG } from "./colors";

type Props = {
	category: PbjCategoryScore;
	// The department's safety-margin band buffer (0–100). > 0 means the scorer matched
	// against tightened cutoffs, so the ladder grows a second, "what it actually took"
	// column plus the note that explains the difference.
	bandRangeBufferPct: number;
	// N/A measures are excluded from the rating entirely — the ladder still shows their
	// would-be standing, but no buffer note (the buffer isn't what's costing them points).
	naExcluded: boolean;
};

/**
 * The flat band ladder for one measure — one row per CMS sub-band with its point value,
 * the scored band highlighted.
 *
 * When a band-range safety buffer is configured the CMS cutoffs alone no longer explain the
 * score (3.803 HPPD sits above the CMS "≥ 3.692 → 60 pts" row yet scores 40), so the table
 * also prints the buffered cutoff the scorer actually required and names the buffer as the
 * reason. Without that, a correctly-buffered score reads as a bug.
 */
export const ThresholdLadderTable: FC<Props> = ({ category, bandRangeBufferPct, naExcluded }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const tint = brandTint(theme);

	const bufferActive = bandRangeBufferPct > 0;
	// Invalid measures are force-scored at the lowest band, so their gap to the CMS ladder is
	// that flag's doing, not the buffer's — same exclusion the breakdown row applies.
	const isInvalid = (category.invalid ?? false) && !naExcluded;
	const pointsCost = bufferActive && !naExcluded && !isInvalid ? bufferPointsCost(category) : 0;
	// The row a user reads off the CMS column for their value — the one the buffer overruled.
	const cmsBand = pointsCost > 0 ? cmsBandFor(category) : null;
	const cmsBandBufferedCutoff = cmsBand ? bufferedCutoffFor(cmsBand, category.kind) : null;

	// Phrase a buffered cutoff the same way the backend phrases the CMS one next to it
	// ("≥ 4.062", "≤ 25.714", "2+ Turnover"). An open-ended tier has no cutoff on its
	// defining side, so it renders as an em dash.
	const cutoffLabel = (band: PbjBand): string => {
		const cutoff = bufferedCutoffFor(band, category.kind);
		if (cutoff === null) return "—";
		switch (cutoff.kind) {
			case "at_least":
				return t("pbj_model.scoring.band.at_least", { value: cutoff.value });
			case "below":
				return t("pbj_model.scoring.band.below", { value: cutoff.value });
			case "at_most":
				return t("pbj_model.scoring.band.at_most", { value: cutoff.value });
			case "count_plus":
				return t("pbj_model.scoring.band.count_plus", { value: cutoff.value });
			case "count":
				return t("pbj_model.scoring.band.count", { value: cutoff.value });
		}
	};

	return (
		<Box>
			<Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1.5, flexWrap: "wrap", rowGap: 0.5 }}>
				<Typography
					variant="caption"
					sx={{ fontWeight: "bold", textTransform: "uppercase", letterSpacing: LETTER_SPACING.LABEL, display: "block" }}
					color="text.secondary"
				>
					{t("pbj_model.scoring.thresholds_title")}
				</Typography>
				{bufferActive && (
					<MetaChip
						label={t("pbj_model.scoring.buffer.chip", { pct: bandRangeBufferPct })}
						icon={<ShieldOutlinedIcon />}
						tone="info"
						tooltip={t("pbj_model.scoring.buffer.chip_tooltip")}
						dataTestId={`pbj-model-category-${category.id}-buffer-chip`}
					/>
				)}
			</Stack>
			<TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
				<Table size="small">
					<TableHead>
						<TableRow sx={{ bgcolor: TABLE_HEAD_BG }}>
							<TableCell sx={{ fontWeight: 700, fontSize: FONT_SIZE.LABEL_XS, py: 1, px: 2 }}>
								{t(category.kind === "admin" ? "pbj_model.scoring.column.count" : "pbj_model.scoring.column.range")}
							</TableCell>
							{bufferActive && (
								<TableCell sx={{ fontWeight: 700, fontSize: FONT_SIZE.LABEL_XS, py: 1, px: 2 }}>{t("pbj_model.scoring.buffer.column")}</TableCell>
							)}
							<TableCell align="right" sx={{ fontWeight: 700, fontSize: FONT_SIZE.LABEL_XS, py: 1, px: 2 }}>
								{t("pbj_model.scoring.column.points")}
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{category.bands.map((band) => {
							// Points are unique per band within a category (10/20/30/.../100 or 5/10/.../50),
							// so matching on `band.points === category.score` reliably identifies the active row.
							const highlighted = band.points === category.score;
							return (
								<TableRow key={`${category.id}-${band.points}`} sx={{ bgcolor: highlighted ? tint : "inherit" }}>
									<TableCell
										sx={{
											py: 1,
											px: 2,
											fontFamily: "monospace",
											fontSize: FONT_SIZE.SECONDARY,
											fontWeight: highlighted ? 700 : 400,
											// With the buffered column present the CMS one is the reference, not the
											// operative number — muted so the eye lands on what was actually required.
											color: bufferActive ? "text.secondary" : "text.primary",
										}}
									>
										{band.displayRange}
									</TableCell>
									{bufferActive && (
										<TableCell sx={{ py: 1, px: 2, fontFamily: "monospace", fontSize: FONT_SIZE.SECONDARY, fontWeight: highlighted ? 700 : 400 }}>
											{cutoffLabel(band)}
										</TableCell>
									)}
									<TableCell align="right" sx={{ py: 1, px: 2, fontSize: FONT_SIZE.SECONDARY, fontWeight: highlighted ? 700 : 500 }}>
										{band.points}
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
			{bufferActive && (
				<InlineHint
					tone={pointsCost > 0 ? "warning" : "info"}
					icon={<ShieldOutlinedIcon />}
					sx={{ mt: 1.5 }}
					dataTestId={`pbj-model-category-${category.id}-buffer-hint`}
				>
					{pointsCost > 0 && cmsBand !== null && cmsBandBufferedCutoff !== null
						? t("pbj_model.scoring.buffer.cost_hint", {
								value: category.value,
								score: category.score,
								cmsScore: cmsBand.points,
								threshold: cutoffLabel(cmsBand),
								cmsThreshold: cmsBand.displayRange,
								pct: bandRangeBufferPct,
							})
						: t("pbj_model.scoring.buffer.hint", { pct: bandRangeBufferPct })}
				</InlineHint>
			)}
		</Box>
	);
};
